import { useEffect, useRef } from 'react';
import { useAppStore } from '../stores/useAppStore';
import { getWeatherMessage } from '../utils/weatherMessages';
import { getTodayDate } from '../utils/dateUtils';

interface InitialMessageOptions {
  onShowMessage: (message: string, duration?: number) => void;
  weatherCondition?: string;
}

// ウェルカムメッセージ（初回訪問時）
const welcomeMessages = [
  'はじめまして、僕は空だよ',
  'このアプリでは、僕と一緒にいろんな時間を過ごせるよ',
  'わからないことがあったら、気軽に聞いてね',
  'それじゃ、これからよろしくね！'
];

// 訪問回数別のメッセージ
const visitCountMessages: Record<number, string[]> = {
  1: ['おかえり！今日も来てくれた時点で大成功だよ！'],
  2: ['2日連続！すごい！継続できてるのがえらい！'],
  3: ['3回目！習慣になってきてる！素晴らしい！'],
  5: ['5回も来てくれた！君の努力、ちゃんと見てるよ'],
  10: [
    '10回達成！これは本当にすごいことだよ！',
    '君の継続力、尊敬しちゃう！'
  ],
  15: ['15回！君と一緒にいられて幸せだよ'],
  20: [
    '20回も！君の頑張り、本当に素敵！',
    '毎回会えるのが楽しみになってきた'
  ],
  30: ['30回達成！もう立派な習慣だね！誇っていいよ！'],
  50: ['50回！半世紀回！君の継続力は本物だ！'],
  75: ['75回も続けられる君、本当にすごい！'],
  100: [
    '100回達成！！これは偉業だよ！',
    '君と一緒に歩んできた証だね。本当にありがとう！'
  ]
};

// その他の訪問時メッセージ
const otherMessages = [
  '今日も来てくれた！それだけで今日は成功だよ！',
  '待ってたよ！君が来てくれて嬉しい！',
  '君が来ただけで今日の目標達成！',
  'また会えた！継続できてる証拠だね！',
  '今日も一緒に過ごせる！最高の一日になりそう',
  'おかえり！アプリを開いた君、えらい！'
];

export function useInitialMessage({ onShowMessage, weatherCondition }: InitialMessageOptions) {
  const hasShownRef = useRef(false);
  const hasShownWeatherRef = useRef(false);
  const { updateUser } = useAppStore();

  useEffect(() => {
    // 既に表示済みなら何もしない
    if (hasShownRef.current) return;
    
    const showMessages = async () => {
      if (hasShownRef.current) return;
      hasShownRef.current = true;
      
      // 訪問回数を更新（1日1回だけ加算。リロードではカウントしない）
      const today = getTodayDate();
      const lastVisitDate = localStorage.getItem('sorariumLastVisitDate');
      const isFirstVisitOfDay = lastVisitDate !== today;
      let visitCount = parseInt(localStorage.getItem('sorariumVisitCount') || '0');
      if (isFirstVisitOfDay) {
        visitCount += 1;
        localStorage.setItem('sorariumVisitCount', String(visitCount));
        localStorage.setItem('sorariumLastVisitDate', today);
      }
      
      // ウェルカム表示済みフラグを確認（訪問回数とは別管理）
      const welcomeShown = localStorage.getItem('sorariumWelcomeShown');
      
      if (!welcomeShown || welcomeShown !== 'true') {
        // 初回訪問：ウェルカムメッセージを順番に表示
        for (let i = 0; i < welcomeMessages.length; i++) {
          await new Promise(resolve => setTimeout(resolve, i === 0 ? 1500 : 3500));
          onShowMessage(welcomeMessages[i], 3000);
        }
        localStorage.setItem('sorariumWelcomeShown', 'true');
        // isFirstTimeフラグを更新
        updateUser({ name: 'ユーザー' }); // これにより isFirstTime が false になる
      } else {
        // 再訪問時
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // 特定の訪問回数のメッセージがあるか確認
        // （節目メッセージはその日最初の訪問だけ。リロードで繰り返さない）
        if (isFirstVisitOfDay && visitCountMessages[visitCount]) {
          const messages = visitCountMessages[visitCount];
          // 複数メッセージがある場合は順番に表示
          for (let i = 0; i < messages.length; i++) {
            if (i > 0) await new Promise(resolve => setTimeout(resolve, 3500));
            onShowMessage(messages[i], 3000);
          }
        } else {
          // その他の訪問時はランダムメッセージを1つ表示
          const randomMessage = otherMessages[Math.floor(Math.random() * otherMessages.length)];
          onShowMessage(randomMessage, 3000);
        }
      }
    };

    showMessages();
  }, [onShowMessage, updateUser]);

  // 天気メッセージは天気APIの取得完了を待ってから別途表示する
  // （マウント直後はweatherConditionが'unknown'のため、上のeffect内では表示できない）
  useEffect(() => {
    if (hasShownWeatherRef.current) return;
    if (!weatherCondition || weatherCondition === 'unknown') return;
    // 初回訪問時はウェルカムメッセージを優先して天気は出さない
    if (localStorage.getItem('sorariumWelcomeShown') !== 'true') return;

    hasShownWeatherRef.current = true;
    // 訪問時の挨拶メッセージと重ならないよう少し待ってから表示
    const timer = setTimeout(() => {
      const weatherMsg = getWeatherMessage(weatherCondition, 'greeting');
      onShowMessage(weatherMsg, 3000);
    }, 5500);
    return () => clearTimeout(timer);
  }, [weatherCondition, onShowMessage]);
}